<template>
  <div class="auth-container">
    <div class="auth-content">
      <div class="auth-box">
        <div class="auth-header">
          <h2>人脸识别认证</h2>
          <p class="auth-desc">请确保光线充足，面部无遮挡</p>
        </div>

        <div class="camera-preview-area">
          <video
            ref="cameraVideoRef"
            autoplay
            playsinline
            class="camera-video"
          ></video>
          <canvas
            ref="cameraCanvasRef"
            style="display: none"
          ></canvas>

          <!-- 拍摄预览 -->
          <div v-if="capturedPhoto" class="captured-preview">
            <img :src="capturedPhoto" alt="拍摄预览" class="preview-image" />
          </div>

          <!-- 错误提示 -->
          <div v-if="cameraError" class="camera-error">
            <p>{{ cameraError }}</p>
            <button @click="retryCamera" class="retry-btn">重试</button>
          </div>

          <!-- 验证中遮罩 -->
          <div v-if="isVerifying" class="verifying-overlay">
            <div class="loading-spinner"></div>
            <p>验证中...</p>
          </div>
        </div>

        <div class="camera-controls">
          <button
            v-if="!capturedPhoto"
            class="capture-btn"
            @click="takePhoto"
            :disabled="!cameraReady"
          >
            📷 拍摄
          </button>
          <button
            v-if="capturedPhoto"
            class="confirm-btn"
            @click="confirmPhoto"
          >
            ✓ 确认
          </button>
          <button
            v-if="capturedPhoto"
            class="retake-btn"
            @click="retakePhoto"
          >
            ↺ 重拍
          </button>
        </div>
      </div>
    </div>

    <!-- 认证加载提示 -->
    <div v-if="isAuthLoading" class="auth-loading-mask">
      <div class="loading-content">
        <div class="loading-spinner large"></div>
        <p>{{ authLoadingText }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AuthCamera",
  data() {
    return {
      mediaStream: null,
      capturedPhoto: null,
      cameraReady: false,
      cameraError: null,
      isVerifying: false,
      isAuthLoading: false,
      authLoadingText: "验证中...",
      // API 地址（相对 /api/）
      targetUrl: "realauth/pictureCompare",
      // 返回地址（相对路径）
      returnUrl: "/train",
      // 回传给原页面的额外参数
      otherParam: "",
      // 用户ID
      uid: 0,
    };
  },
  mounted() {
    // 禁止页面滚动
    document.body.style.overflow = "hidden";
    // 解析路由参数
    this.parseRouteParams();
    // 打开摄像头
    this.openCamera();
    // 监听ESC键
    document.addEventListener("keydown", this.handleEscKey);
  },
  beforeDestroy() {
    // 恢复页面滚动
    document.body.style.overflow = "";
    this.stopCameraStream();
    document.removeEventListener("keydown", this.handleEscKey);
  },
  methods: {
    /**
     * 解析路由参数，确定调用哪个API以及返回地址
     */
    parseRouteParams() {
      const urlParams = new URLSearchParams(window.location.search);
      let paramStr = "";
      for (const [key, value] of urlParams) {
        if (key === "uid") {
          this.uid = value;
          continue;
        }
        if (key === "page") {
          if (value === "study") {
            this.targetUrl = "realauth/pictureCompare";
            this.returnUrl = "/train";
          } else {
            this.targetUrl = "realauth/examPictureCompare";
            this.returnUrl = "/exam";
          }
        }
        paramStr += key + "=" + value + "&";
      }
      this.otherParam = paramStr.substring(0, paramStr.length - 1);
    },

    /**
     * 打开摄像头
     */
    async openCamera() {
      // 检查HTTPS环境
      if (location.protocol !== "https:" && location.hostname !== "localhost") {
        this.cameraError = "摄像头功能需要在 HTTPS 环境下使用";
        return;
      }

      // 检查浏览器支持
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = "您的浏览器不支持摄像头功能";
        return;
      }

      try {
        const constraints = {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: "user",
          },
          audio: false,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.mediaStream = stream;

        const videoElement = this.$refs.cameraVideoRef;
        if (videoElement) {
          videoElement.srcObject = stream;

          await new Promise((resolve, reject) => {
            if (!videoElement) {
              reject(new Error("Video element not found"));
              return;
            }
            videoElement.onloadedmetadata = () => {
              videoElement
                .play()
                .then(resolve)
                .catch(reject);
            };
            videoElement.onerror = reject;
          });

          this.cameraReady = true;
          this.cameraError = null;
        }
      } catch (error) {
        console.error("启动摄像头失败:", error);
        this.cameraReady = false;

        if (error.name === "NotAllowedError") {
          this.cameraError = "请授权摄像头权限后重试";
        } else if (error.name === "NotFoundError") {
          this.cameraError = "未检测到摄像头设备";
        } else {
          this.cameraError =
            "无法打开摄像头：" + (error.message || "请检查权限设置");
        }
      }
    },

    /**
     * 重试打开摄像头
     */
    retryCamera() {
      this.cameraError = null;
      this.cameraReady = false;
      this.capturedPhoto = null;
      this.stopCameraStream();
      setTimeout(() => {
        this.openCamera();
      }, 500);
    },

    /**
     * 拍摄照片
     */
    takePhoto() {
      const videoElement = this.$refs.cameraVideoRef;
      const canvasElement = this.$refs.cameraCanvasRef;

      if (!videoElement || !canvasElement) {
        this.$message.error("无法获取视频画面");
        return;
      }

      if (videoElement.readyState !== 4 || videoElement.videoWidth === 0) {
        this.$message.error("视频未就绪，请稍后重试");
        return;
      }

      canvasElement.width = videoElement.videoWidth;
      canvasElement.height = videoElement.videoHeight;

      const context = canvasElement.getContext("2d");
      if (!context) {
        this.$message.error("无法创建画布上下文");
        return;
      }

      context.drawImage(
        videoElement,
        0,
        0,
        canvasElement.width,
        canvasElement.height
      );
      this.capturedPhoto = canvasElement.toDataURL("image/jpeg", 0.8);
      this.$message.success("拍照成功");

      // 停止摄像头流
      if (this.mediaStream) {
        this.mediaStream.getVideoTracks().forEach((track) => track.stop());
        this.mediaStream = null;
        this.cameraReady = false;
      }
    },

    /**
     * 照片比对认证
     */
    async pictureDeal() {
      if (!this.capturedPhoto) {
        this.$message.warning("请先拍照");
        return;
      }

      this.isVerifying = true;

      try {
        const param = {
          base64: this.capturedPhoto,
          uid: this.uid,
          token: localStorage.getItem("token-qzb") || "",
        };
        const res = await this.post(this.targetUrl, param);

        if (res.code == 1 && res.data.passed === "T") {
          this.$message.success("认证成功");
          this.goBack(true);
        } else {
          this.$message.error("认证失败，请重新拍摄");
          this.goBack(false);
        }
      } catch (error) {
        console.error("认证失败:", error);
        this.$message.error(error.message || "认证失败，请重试");
        this.capturedPhoto = null;
        this.openCamera();
      } finally {
        this.isVerifying = false;
      }
    },

    /**
     * 确认照片
     */
    async confirmPhoto() {
      if (!this.capturedPhoto) {
        this.$message.warning("请先拍照");
        return;
      }
      await this.pictureDeal();
    },

    /**
     * 重拍
     */
    retakePhoto() {
      this.capturedPhoto = null;
      this.openCamera();
    },

    /**
     * 停止摄像头
     */
    stopCameraStream() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => {
          track.stop();
        });
        this.mediaStream = null;
      }
      const videoElement = this.$refs.cameraVideoRef;
      if (videoElement) {
        videoElement.srcObject = null;
      }
      this.cameraReady = false;
    },

    /**
     * 返回原页面（使用 window.location.href 触发完整刷新以重新校验认证状态）
     * @param {boolean} success 是否认证成功
     */
    goBack(success) {
      let url = this.returnUrl + "?picture=" + (success ? "1" : "0");
      if (this.otherParam) {
        url += "&" + this.otherParam;
      }
      window.location.href = url;
    },

    /**
     * ESC键处理
     */
    handleEscKey(event) {
      if (event.key === "Escape") {
        this.$message.warning("请完成拍照认证后再离开");
      }
    },
  },
};
</script>

<style scoped>
.auth-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  height: 100vh;
  display: flex;
}

.auth-content {
  width: 100%;
  max-width: 600px;
  padding: 20px;
  margin: auto;
}

.auth-box {
  background-color: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.auth-header {
  text-align: center;
  padding: 30px 20px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.auth-header h2 {
  margin: 0 0 10px;
  font-size: 24px;
}

.auth-desc {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

.camera-preview-area {
  position: relative;
  background-color: #000;
  min-height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.camera-video {
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: cover;
}

.captured-preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.camera-error {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  text-align: center;
  padding: 20px;
}

.verifying-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  z-index: 10;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.loading-spinner.large {
  width: 60px;
  height: 60px;
  border-width: 4px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.retry-btn {
  padding: 8px 20px;
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 15px;
}

.retry-btn:hover {
  background-color: #40a9ff;
}

.camera-controls {
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  background-color: #f5f5f5;
}

.capture-btn,
.confirm-btn,
.retake-btn {
  padding: 12px 30px;
  border: none;
  border-radius: 30px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.capture-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.capture-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.capture-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 20px rgba(102, 126, 234, 0.4);
}

.confirm-btn {
  background-color: #52c41a;
  color: white;
}

.confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 20px rgba(82, 196, 26, 0.4);
}

.retake-btn {
  background-color: #ff7875;
  color: white;
}

.retake-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 20px rgba(255, 120, 117, 0.4);
}

/* 认证加载遮罩 */
.auth-loading-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.loading-content {
  background-color: white;
  padding: 30px 40px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.loading-content p {
  margin-top: 15px;
  font-size: 16px;
  color: #333;
}
</style>
